import type { WorkoutLayer } from '@strength/db-schema';

import type { WorkoutImportValidationResult } from './useWorkoutImport';

// The suggested-match shapes are declared locally here to keep the helper
// pure and self-contained — the web app does not depend on the
// trpc-server's internal modules.

export interface SuggestedExerciseMatch {
  name: string;
  libraryExerciseId: string;
  suggestedName: string;
  suggestedId: string;
}

export interface SuggestedMetricMatch {
  name: string;
  type: string;
  metricId: string;
  suggestedName: string;
  suggestedId: string;
}

/**
 * Apply AI-suggested entity matches into a parsed `WorkoutLayer[]`, substituting
 * each temp UUID reference (from `missingExerciseMapping` / `missingMetricMapping`)
 * with its `suggestedId` (the id of the existing org entity the AI matched it to).
 *
 * The substitution touches the four places where entity ids live inside a layer:
 *  - `exerciseAdds[].libraryExerciseId` / `exerciseEdits[].libraryExerciseId`
 *    (exercise references)
 *  - `exerciseMetricAdds[].metricId` (metric references on exercises)
 *  - `setMetrics[].metricId` (metric references on sets)
 *
 * Returns a new array of layers (deep-copied via structuredClone so the parsed
 * template is never mutated).
 */
export function applySuggestedMappings(
  layers: WorkoutLayer[],
  suggestedExerciseMapping: Record<string, SuggestedExerciseMatch>,
  suggestedMetricMapping: Record<string, SuggestedMetricMatch> = {},
): WorkoutLayer[] {
  return layers.map((layer) => {
    const next: WorkoutLayer = structuredClone(layer);

    for (const add of next.exerciseAdds) {
      const match = suggestedExerciseMapping[add.libraryExerciseId];
      if (match) {
        add.libraryExerciseId = match.suggestedId;
      }
    }

    for (const edit of next.exerciseEdits) {
      if (edit.libraryExerciseId == null) continue;
      const match = suggestedExerciseMapping[edit.libraryExerciseId];
      if (match) {
        edit.libraryExerciseId = match.suggestedId;
      }
    }

    for (const metric of next.exerciseMetricAdds) {
      const match = suggestedMetricMapping[metric.metricId];
      if (match) {
        metric.metricId = match.suggestedId;
      }
    }

    for (const metric of next.setMetrics) {
      const match = suggestedMetricMapping[metric.metricId];
      if (match) {
        metric.metricId = match.suggestedId;
      }
    }

    return next;
  });
}

/**
 * The names of the missing exercises that are still unresolved (no accepted
 * suggestion). Missing exercises with an accepted suggestion are pre-substituted
 * to the suggested existing org exercise (so they will NOT be auto-created on
 * save) and therefore don't belong in the "to be created" warning.
 */
export function unresolvedMissingExerciseNames(
  result: Pick<
    WorkoutImportValidationResult,
    'missingExerciseMapping' | 'suggestedExerciseMapping'
  >,
): string[] {
  return Object.entries(result.missingExerciseMapping)
    .filter(([tempUuid]) => !(tempUuid in result.suggestedExerciseMapping))
    .map(([, exercise]) => exercise.name);
}

/**
 * Whether the parsed result has any missing metrics that the admin has not yet
 * resolved via an accepted suggestion. A missing metric is "unresolved" when its
 * temp UUID key in `missingMetricMapping` has no corresponding entry in
 * `suggestedMetricMapping`.
 *
 * This drives the `canSave` gate: unresolved missing metrics block Save and
 * render the missing-metrics error banner.
 */
export function hasUnresolvedMissingMetrics(
  result: Pick<
    WorkoutImportValidationResult,
    'missingMetricMapping' | 'suggestedMetricMapping'
  >,
): boolean {
  return Object.keys(result.missingMetricMapping).some(
    (tempUuid) => !(tempUuid in result.suggestedMetricMapping),
  );
}

/**
 * The names of the missing metrics that are still unresolved (no accepted
 * suggestion). Used by the results view to render the error banner — resolved
 * metrics (those with an accepted `suggestedMetricMapping` entry) are not
 * blocking, so they don't belong in the banner.
 */
export function unresolvedMissingMetricNames(
  result: Pick<
    WorkoutImportValidationResult,
    'missingMetricMapping' | 'suggestedMetricMapping'
  >,
): string[] {
  return Object.entries(result.missingMetricMapping)
    .filter(([tempUuid]) => !(tempUuid in result.suggestedMetricMapping))
    .map(([, metric]) => metric.name);
}

/**
 * The set of metric temp UUIDs that are still unresolved (no accepted suggestion).
 * Used to strip their references from the template before it reaches the editor.
 */
function unresolvedMetricTempIds(
  result: Pick<
    WorkoutImportValidationResult,
    'missingMetricMapping' | 'suggestedMetricMapping'
  >,
): Set<string> {
  return new Set(
    Object.keys(result.missingMetricMapping).filter(
      (tempUuid) => !(tempUuid in result.suggestedMetricMapping),
    ),
  );
}

/**
 * Remove every reference to a given metric id from a `WorkoutLayer[]` — both
 * the exercise-level metric headers (`exerciseMetricAdds`) and the per-set
 * metric values (`setMetrics`). Used by the review banner's "Remove" action to
 * strip a suggested metric the admin rejected, so no lingering id (empty or
 * suggested) reaches `saveTemplate`. The editor is remounted afterward so its
 * internal copy re-initializes from the stripped template.
 *
 * Returns a new array of layers (deep-copied so the working template is never
 * mutated).
 */
export function removeMetricFromLayers(
  layers: WorkoutLayer[],
  metricId: string,
): WorkoutLayer[] {
  return layers.map((layer) => {
    const next: WorkoutLayer = structuredClone(layer);
    next.exerciseMetricAdds = next.exerciseMetricAdds.filter(
      (m) => m.metricId !== metricId,
    );
    next.setMetrics = next.setMetrics.filter((m) => m.metricId !== metricId);
    return next;
  });
}

/**
 * Remove metric references the editor can't resolve from a `WorkoutLayer[]`.
 *
 * `WorkoutTemplateSheet` calls `useMetrics().getMetric(id)` during render, which
 * throws for any metric id that doesn't exist in the org. After
 * `applySuggestedMappings` runs, the only unresolved metric ids left in the
 * template are temp UUIDs whose missing metric has no accepted suggestion —
 * those are surfaced by the error banner instead, so we drop them from the
 * editor's `exerciseMetricAdds` and `setMetrics` to prevent a render crash.
 *
 * Returns a new array of layers (deep-copied so the parsed template is never
 * mutated).
 */
/**
 * Repoint every reference to `fromExerciseId` at `toExerciseId`.
 *
 * Used by the results view's "Add" action: once the exercise has actually been
 * created in the org, the template's temp-UUID `libraryExerciseId` is swapped
 * for the real new id, so the saved template only references real rows.
 *
 * Returns a new array of layers (deep-copied so the working template is never
 * mutated).
 */
export function substituteExerciseId(
  layers: WorkoutLayer[],
  fromExerciseId: string,
  toExerciseId: string,
): WorkoutLayer[] {
  return layers.map((layer) => {
    const next: WorkoutLayer = structuredClone(layer);
    for (const add of next.exerciseAdds) {
      if (add.libraryExerciseId === fromExerciseId) {
        add.libraryExerciseId = toExerciseId;
      }
    }
    for (const edit of next.exerciseEdits) {
      if (edit.libraryExerciseId === fromExerciseId) {
        edit.libraryExerciseId = toExerciseId;
      }
    }
    return next;
  });
}

/**
 * Remove an exercise from a `WorkoutLayer[]` along with everything hanging off
 * it — its metric headers, its sets, and those sets' metric values — so no
 * orphaned rows reach `saveTemplate`.
 *
 * Used by the results view's "Remove" action when the admin doesn't want an
 * exercise that isn't in the org.
 *
 * Returns a new array of layers (deep-copied so the working template is never
 * mutated).
 */
export function removeExerciseFromLayers(
  layers: WorkoutLayer[],
  libraryExerciseId: string,
): WorkoutLayer[] {
  return layers.map((layer) => {
    const next: WorkoutLayer = structuredClone(layer);

    // The workoutExerciseIds being dropped — everything else keys off these.
    const droppedWorkoutExerciseIds = new Set(
      next.exerciseAdds
        .filter((e) => e.libraryExerciseId === libraryExerciseId)
        .map((e) => e.workoutExerciseId),
    );
    if (droppedWorkoutExerciseIds.size === 0) return next;

    next.exerciseAdds = next.exerciseAdds.filter(
      (e) => !droppedWorkoutExerciseIds.has(e.workoutExerciseId),
    );
    next.exerciseEdits = next.exerciseEdits.filter(
      (e) => !droppedWorkoutExerciseIds.has(e.workoutExerciseId),
    );
    next.exerciseMetricAdds = next.exerciseMetricAdds.filter(
      (m) => !droppedWorkoutExerciseIds.has(m.workoutExerciseId),
    );

    // Superset/alternate rows reference exercises by workoutExerciseId — drop
    // any grouping rows pointing at an exercise being removed so no orphaned
    // rows reference a workoutExerciseId that no longer has an exerciseAdds
    // entry in the saved template JSON. The matching *Edit rows key off the
    // grouping-row id (workoutSupersetExerciseId / workoutAlternateExerciseId),
    // so derive those from the Adds rows being dropped.
    const droppedSupersetExerciseIds = new Set(
      next.supersetExerciseAdds
        .filter((s) => droppedWorkoutExerciseIds.has(s.workoutExerciseId))
        .map((s) => s.workoutSupersetExerciseId),
    );
    next.supersetExerciseAdds = next.supersetExerciseAdds.filter(
      (s) => !droppedWorkoutExerciseIds.has(s.workoutExerciseId),
    );
    next.supersetExerciseEdits = next.supersetExerciseEdits.filter(
      (s) => !droppedSupersetExerciseIds.has(s.workoutSupersetExerciseId),
    );
    const droppedAlternateExerciseIds = new Set(
      next.alternateExerciseAdds
        .filter((s) => droppedWorkoutExerciseIds.has(s.workoutExerciseId))
        .map((s) => s.workoutAlternateExerciseId),
    );
    next.alternateExerciseAdds = next.alternateExerciseAdds.filter(
      (s) => !droppedWorkoutExerciseIds.has(s.workoutExerciseId),
    );
    next.alternateExerciseEdits = next.alternateExerciseEdits.filter(
      (s) => !droppedAlternateExerciseIds.has(s.workoutAlternateExerciseId),
    );

    // Sets belonging to the dropped exercises, plus their metric values.
    const droppedWorkoutSetIds = new Set(
      next.setAdds
        .filter((s) => droppedWorkoutExerciseIds.has(s.workoutExerciseId))
        .map((s) => s.workoutSetId),
    );
    next.setAdds = next.setAdds.filter(
      (s) => !droppedWorkoutExerciseIds.has(s.workoutExerciseId),
    );
    next.setEdits = next.setEdits.filter(
      (s) => !droppedWorkoutExerciseIds.has(s.workoutExerciseId),
    );
    next.setMetrics = next.setMetrics.filter(
      (m) => !droppedWorkoutSetIds.has(m.workoutSetId),
    );

    return next;
  });
}

/**
 * Drop every metric reference whose id is not in `knownMetricIds`.
 *
 * Belt-and-braces guard for the embedded sheet: `useMetrics().getMetric(id)`
 * throws `Could not find metric with id …` whenever the org's metric list is
 * loaded but the requested id isn't in it. That happens for any id the parser
 * (or the mock) invents that doesn't exist in the target org — including
 * *suggested* ids, which `stripUnresolvedMetricRefs` deliberately keeps.
 *
 * Applied right before handing layers to the sheet, once the org's real metrics
 * are known. Pass an empty set to disable (returns the layers untouched) so we
 * don't strip everything while metrics are still loading.
 */
export function stripUnknownMetricRefs(
  layers: WorkoutLayer[],
  knownMetricIds: Set<string>,
): WorkoutLayer[] {
  if (knownMetricIds.size === 0) return layers;

  return layers.map((layer) => {
    const next: WorkoutLayer = structuredClone(layer);
    next.exerciseMetricAdds = next.exerciseMetricAdds.filter((m) =>
      knownMetricIds.has(m.metricId),
    );
    next.setMetrics = next.setMetrics.filter((m) =>
      knownMetricIds.has(m.metricId),
    );
    return next;
  });
}

export function stripUnresolvedMetricRefs(
  layers: WorkoutLayer[],
  result: Pick<
    WorkoutImportValidationResult,
    'missingMetricMapping' | 'suggestedMetricMapping'
  >,
): WorkoutLayer[] {
  const unresolved = unresolvedMetricTempIds(result);
  if (unresolved.size === 0) return layers.map((l) => structuredClone(l));

  return layers.map((layer) => {
    const next: WorkoutLayer = structuredClone(layer);
    next.exerciseMetricAdds = next.exerciseMetricAdds.filter(
      (m) => !unresolved.has(m.metricId),
    );
    next.setMetrics = next.setMetrics.filter(
      (m) => !unresolved.has(m.metricId),
    );
    return next;
  });
}
